(function () {
    class NewsGraph2DVisualizer {
        constructor(container) {
            this.container = container;
            this._destroyed = false;
            this.orbitSphereVisible = true;
        }

        mount(data) {
            this._data = data;
            this._nodes = window.NewsGraph.selectNodes(data);
            this._links = window.NewsGraph.selectLinks(data.relationships || [], this._nodes);
            this._tickerPriceMap = NewsProxyAPIManager.buildTickerPriceMap(data);
            this._preIpoSymbols = new Set(
                (data?.tickers || [])
                    .filter(t => t.possible_pre_ipo === true)
                    .map(t => String(t.symbol || '').toUpperCase().trim())
            );

            this._isLight = document.body.classList.contains('light') || document.body.classList.contains('cb-light');

            this._initAframeScene();
            this._replaceGeometriesWithCircles();

            const layout = window.NewsGraph.compute2DLayout(this._nodes, data);
            const { narrativeNodes, sortedTickerNodes, activeSectorProxies, sectorsMap } = layout;

            this._nodes.forEach(node => {
                const el = this._meshMap[node.id];
                if (el) {
                    const isNarrative = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
                    if (isNarrative) {
                        this._innerGroupEl.appendChild(el);
                    } else {
                        this._outerGroupEl.appendChild(el);
                    }
                }
            });

            const stockMap = this._getTickerPriceMap();
            const tickerPositions = window.NewsGraph.computeTickerRingPositions(sortedTickerNodes, 4.0);
            tickerPositions.forEach(({ node, x, y, angle }) => {
                const el = this._meshMap[node.id];
                if (!el) return;
                el.object3D.position.set(x, y, 0);
                const isFlipped = window.NewsGraph.isConeFlipped(node, stockMap, this._preIpoSymbols);
                // CircleGeometry(…, 3) has its tip on +X. Rotate 90° so positive
                // tickers point screen-up and negative tickers point screen-down.
                el.userData.staticConeRotation = Math.PI / 2 + (isFlipped ? Math.PI : 0);
                el.object3D.rotation.set(0, 0, el.userData.staticConeRotation);
                node.angle2D = angle;
            });

            const THREE = window.THREE || (window.AFRAME && window.AFRAME.THREE);
            const SECTOR_INFO = window.NewsGraph.SECTOR_INFO;
            const arcDescs = window.NewsGraph.computeSectorArcs(
                sortedTickerNodes, sectorsMap, activeSectorProxies
            );

            this._sectorHelpers = [];
            arcDescs.forEach(arc => {
                const pad = Math.min(0.03, arc.arcLen * 0.1);
                const thetaStart = arc.startAngle + pad;
                const thetaLength = Math.max(0.02, arc.arcLen - pad * 2);

                const ringGeo = new THREE.RingGeometry(arc.innerRadius, arc.outerRadius, 32, 1, thetaStart, thetaLength);
                const ringMat = new THREE.MeshBasicMaterial({
                    color: new THREE.Color(arc.color),
                    transparent: true,
                    opacity: 0.22,
                    side: THREE.DoubleSide,
                    depthWrite: false
                });
                const bandMesh = new THREE.Mesh(ringGeo, ringMat);
                bandMesh.position.z = -0.02;
                bandMesh.renderOrder = 0;
                this._outerGroup.add(bandMesh);

                const borderSegments = 32;
                const outerPts = [];
                const innerPts = [];
                for (let s = 0; s <= borderSegments; s++) {
                    const a = thetaStart + (s / borderSegments) * thetaLength;
                    outerPts.push(new THREE.Vector3(arc.outerRadius * Math.cos(a), arc.outerRadius * Math.sin(a), -0.01));
                    innerPts.push(new THREE.Vector3(arc.innerRadius * Math.cos(a), arc.innerRadius * Math.sin(a), -0.01));
                }
                const borderMat = new THREE.LineBasicMaterial({
                    color: new THREE.Color(arc.color),
                    transparent: true,
                    opacity: 0.55
                });
                const outerLine = new THREE.Line(new THREE.BufferGeometry().setFromPoints(outerPts), borderMat);
                const innerLine = new THREE.Line(new THREE.BufferGeometry().setFromPoints(innerPts), borderMat);
                outerLine.renderOrder = 1;
                innerLine.renderOrder = 1;
                this._outerGroup.add(outerLine);
                this._outerGroup.add(innerLine);

                const helper = new THREE.Object3D();
                helper.position.set(4.46 * Math.cos(arc.midAngle), 4.46 * Math.sin(arc.midAngle), 0);
                this._outerGroup.add(helper);

                this._sectorHelpers.push({
                    helper,
                    name: arc.name,
                    color: arc.color,
                    angle: arc.midAngle
                });
            });

            const separationPoints = [];
            const separationRadius = 2.95;
            const separationSegments = 128;
            for (let i = 0; i <= separationSegments; i += 1) {
                const angle = (i / separationSegments) * Math.PI * 2;
                separationPoints.push(new THREE.Vector3(
                    separationRadius * Math.cos(angle),
                    separationRadius * Math.sin(angle),
                    0
                ));
            }
            this._separationRing = new THREE.Line(
                new THREE.BufferGeometry().setFromPoints(separationPoints),
                new THREE.LineDashedMaterial({
                    color: 0xffffff,
                    transparent: true,
                    opacity: 0.9,
                    dashSize: 0.14,
                    gapSize: 0.10,
                    depthWrite: false,
                    depthTest: false
                })
            );
            this._separationRing.computeLineDistances();
            this._separationRing.position.z = -0.01;
            this._separationRing.visible = this.orbitSphereVisible !== false;
            this._pivot.add(this._separationRing);

            const narrativePositions = window.NewsGraph.computeNarrativeRingPositions(narrativeNodes, 2.0);
            narrativePositions.forEach(({ node, x, y, angle }) => {
                const el = this._meshMap[node.id];
                if (el) {
                    el.object3D.position.set(x, y, 0);
                    el.object3D.rotation.set(0, 0, 0);
                }
                node.angle2D = angle;
            });

            const quadGeo = new THREE.PlaneGeometry(1, 1);
            this._edgesGroup = new THREE.Group();
            this._pivot.add(this._edgesGroup);
            this._edgeMeshes = [];

            this._links.forEach(link => {
                const thickness = 0.02 + (link.strength / 100) * 0.04;
                const opacity = 0.28 + (link.strength / 100) * 0.38;
                const mat = new THREE.MeshBasicMaterial({
                    color: 0x94a3b8,
                    transparent: true,
                    opacity: opacity,
                    depthWrite: false
                });
                const mesh = new THREE.Mesh(quadGeo, mat);
                mesh.userData = {
                    sourceId: link.source,
                    targetId: link.target,
                    thickness,
                    baseOpacity: opacity
                };
                mesh.renderOrder = 1;
                this._edgesGroup.add(mesh);
                this._edgeMeshes.push(mesh);
            });

            this._selectedNodeEl = null;

            this._labelMap = window.NewsGraph.NewsGraphBuilder.buildLabels(this._nodes, this._labelRoot);

            this._sectorLabels = [];
            this._sectorHelpers.forEach(item => {
                const div = document.createElement('div');
                div.className = 'news-graph-sector-label';
                div.style.cssText = [
                    'position:absolute',
                    'font-size:8.5px',
                    'font-weight:700',
                    'white-space:nowrap',
                    'pointer-events:none',
                    'transform:translate(-50%,-50%)',
                    'font-family:inherit',
                    'text-transform:uppercase',
                    'letter-spacing:0.09em',
                    'text-shadow: 0 1px 3px rgba(0,0,0,0.8)',
                    `color:${item.color}`
                ].join(';');
                div.textContent = item.name;
                this._labelRoot.appendChild(div);
                this._sectorLabels.push({ div, item });
            });

            this._updateTheme();

            this._themeObserver = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        const isLight = document.body.classList.contains('light') || document.body.classList.contains('cb-light');
                        if (isLight !== this._isLight) {
                            this._isLight = isLight;
                            this._updateTheme();
                        }
                    }
                });
            });
            this._themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

            this._controls = new window.NewsGraph.NewsGraph2DControls(this.container, this._pivot, this._camera);
            this._controls.onClick = (x, y) => this._handleClick(x, y);

            this._updateLayoutAndCamera();

            this._resizeObserver = new ResizeObserver(() => {
                this._updateLayoutAndCamera();
            });
            this._resizeObserver.observe(this.container);

            const loadPromises = this._nodeEls.map(el => {
                if (el.hasLoaded) return Promise.resolve();
                return new Promise(resolve => el.addEventListener('loaded', resolve, { once: true }));
            });
            Promise.all(loadPromises).then(() => {
                if (!this._destroyed) {
                    this._replaceGeometriesWithCircles();
                    this._updateNodeVisuals();
                }
            });

            this._animate();
        }

        _handleClick(clientX, clientY) {
            const rect = this.container.getBoundingClientRect();
            const x = ((clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((clientY - rect.top) / rect.height) * 2 + 1;

            if (!this._raycaster) this._raycaster = new THREE.Raycaster();
            this._raycaster.setFromCamera({ x, y }, this._camera);

            const nodeMeshes = this._nodeEls.map(el => el.getObject3D('mesh')).filter(Boolean);
            const intersects = this._raycaster.intersectObjects(nodeMeshes, false);

            if (intersects.length > 0) {
                const clickedMesh = intersects[0].object;
                const clickedNodeEl = this._nodeEls.find(el => el.getObject3D('mesh') === clickedMesh);
                if (clickedNodeEl) {
                    const nodeId = Object.keys(this._meshMap).find(id => this._meshMap[id] === clickedNodeEl);
                    if (nodeId) {
                        const node = this._nodes.find(n => n.id === nodeId);
                        if (node && this.onNodeClick) {
                            if (this._selectedNodeEl === clickedNodeEl) {
                                this._selectedNodeEl = null;
                                this._updateNodeVisuals();
                                if (this._controls) {
                                    this._controls.enableAutoRotate();
                                }
                                this.onNodeClick(null);
                            } else {
                                this._selectedNodeEl = clickedNodeEl;
                                this._updateNodeVisuals();
                                if (this._controls) {
                                    this._controls.disableAutoRotate();
                                }
                                this.onNodeClick(node);
                            }
                        }
                    }
                }
            } else {
                if (this._selectedNodeEl !== null) {
                    this._selectedNodeEl = null;
                    this._updateNodeVisuals();
                    if (this._controls) {
                        this._controls.enableAutoRotate();
                    }
                    if (this.onNodeClick) {
                        this.onNodeClick(null);
                    }
                }
            }
        }

        _updateNodeVisuals() {
            if (!this._nodeEls || this._nodeEls.length === 0) return;
            const THREE = window.THREE || (window.AFRAME && window.AFRAME.THREE);
            const stockMap = this._getTickerPriceMap();

            let selectedNodeId = null;
            if (this._selectedNodeEl) {
                selectedNodeId = Object.keys(this._meshMap).find(id => this._meshMap[id] === this._selectedNodeEl) || null;
            }
            const highlightedIds = this._getHighlightedIds(selectedNodeId);

            this._nodeEls.forEach(el => {
                const node = this._nodes.find(n => this._meshMap[n.id] === el);
                if (!node) return;

                const isNarrative = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
                const isSelected = (el === this._selectedNodeEl);
                const isConnected = !!highlightedIds && highlightedIds.has(node.id) && !isSelected;
                const isDimmed = !!highlightedIds && !highlightedIds.has(node.id);

                el.userData.isDimmed = isDimmed;
                el.userData.isSelectionDimmed = isDimmed;

                const mesh = el.getObject3D('mesh');
                if (mesh && mesh.material && mesh.material.isMeshBasicMaterial) {
                    const colorStr = window.NewsGraph.getNodeColor(node, stockMap, this._preIpoSymbols);
                    const baseOpacity = isNarrative ? 0.90 : 0.75;

                    mesh.material.color.set(colorStr);
                    mesh.material.transparent = true;
                    mesh.material.opacity = isDimmed ? 0.30 : (isConnected ? 1.0 : baseOpacity);
                    mesh.material.depthWrite = !isDimmed;
                    mesh.material.needsUpdate = true;
                }

                const isFresh = node.freshness && (node.freshness.status === 'inflection' || node.freshness.status === 'new');
                if (isFresh) {
                    el.userData.isFresh = !isDimmed;
                    const isNeutral = !node.direction || node.direction === 'neutral';
                    const isLight = this._isLight;
                    const baseColorHex = window.NewsGraph.getColor(node.direction);
                    const colorStr = '#' + baseColorHex.toString(16).padStart(6, '0');

                    if (isLight) {
                        el.userData.opacityMin = isNarrative ? 0.75 : 0.6;
                        el.userData.opacityMax = isNarrative ? 0.98 : 0.88;
                    } else {
                        el.userData.opacityMin = isNarrative ? 0.70 : 0.55;
                        el.userData.opacityMax = isNarrative ? 1.0 : 0.92;
                    }

                    const glowSprite = el.getObject3D('glowSprite');
                    if (glowSprite) {
                        if (isLight) {
                            glowSprite.material.blending = THREE.NormalBlending;
                            glowSprite.material.color.set(isNeutral ? '#666677' : colorStr);
                        } else {
                            glowSprite.material.blending = THREE.AdditiveBlending;
                            glowSprite.material.color.set(isNeutral ? '#ccccdd' : colorStr);
                        }
                        glowSprite.material.needsUpdate = true;
                    }
                } else {
                    el.userData.isFresh = false;
                }

                const label = this._labelMap && this._labelMap[node.id];
                if (label) {
                    label.style.opacity = isDimmed ? '0.28' : '1';
                    label.style.filter = (isSelected || isConnected) ? 'drop-shadow(0 0 4px #38bdf8)' : '';
                    label.style.fontWeight = (isSelected || isConnected) ? '700' : '';
                }

                const glowSprite = el.getObject3D('glowSprite');
                if (glowSprite && glowSprite.material && isConnected) {
                    glowSprite.material.opacity = 0.9;
                    glowSprite.material.needsUpdate = true;
                }

                this._updateSelectionOutline2D(el, el.userData.radius, isSelected, isNarrative);
            });

            if (this._edgeMeshes) {
                this._edgeMeshes.forEach(mesh => {
                    if (!mesh.material) return;
                    mesh.renderOrder = 10;
                    if (!selectedNodeId) {
                        mesh.material.color.set(0x94a3b8);
                        mesh.material.opacity = mesh.userData.baseOpacity || 0.5;
                    } else {
                        const isConnected =
                            mesh.userData.sourceId === selectedNodeId || mesh.userData.targetId === selectedNodeId;
                        mesh.material.color.set(isConnected ? 0x38bdf8 : 0x94a3b8);
                        mesh.material.opacity = isConnected
                            ? Math.min(1.0, (mesh.userData.baseOpacity || 0.5) + 0.35)
                            : 0.1;
                    }
                    mesh.material.depthTest = true;
                    mesh.material.needsUpdate = true;
                });
            }
        }

        _getHighlightedIds(selectedNodeId) {
            if (!selectedNodeId) return null;
            const selectedId = String(selectedNodeId);
            const ids = new Set([selectedId]);
            const endpointId = endpoint => String(endpoint && endpoint.id ? endpoint.id : endpoint || '');
            this._links.forEach(link => {
                const sourceId = endpointId(link.source || link.source_id);
                const targetId = endpointId(link.target || link.target_id);
                if (sourceId === selectedId) ids.add(targetId);
                if (targetId === selectedId) ids.add(sourceId);
            });
            return ids;
        }

        _updateSelectionOutline2D(el, r, isSelected, isNarrative) {
            if (isSelected) {
                let outlineEl = el.querySelector('.selection-outline-2d');
                const radius = r || 0.5;
                const innerR = radius * 1.05;
                const outerR = radius * 1.18;
                const segments = isNarrative ? 32 : 3;
                const geomAttr = `primitive: ring; radiusInner: ${innerR}; radiusOuter: ${outerR}; segmentsTheta: ${segments}`;

                if (!outlineEl) {
                    const old = el.querySelector('.selection-outline');
                    if (old) old.remove();

                    outlineEl = document.createElement('a-entity');
                    outlineEl.classList.add('selection-outline-2d');
                    outlineEl.setAttribute('geometry', geomAttr);
                    outlineEl.setAttribute('material', {
                        color: '#00ffff',
                        side: 'double',
                        transparent: true,
                        opacity: 0.85,
                        shader: 'flat',
                        depthWrite: false
                    });
                    outlineEl.object3D.position.set(0, 0, 0.005);
                    el.appendChild(outlineEl);
                } else {
                    outlineEl.setAttribute('geometry', geomAttr);
                }
            } else {
                const outlineEl = el.querySelector('.selection-outline-2d');
                if (outlineEl) outlineEl.remove();
                const old = el.querySelector('.selection-outline');
                if (old) old.remove();
            }
        }

        _updateLayoutAndCamera() {
            const aspect = this.container.clientWidth / this.container.clientHeight;
            if (!aspect || isNaN(aspect)) return;

            this._adjustCameraToFit();
        }

        _adjustCameraToFit() {
            const maxR = 4.8;
            const padding = 1.15;
            const fitR = maxR * padding;

            const camera = this._camera;
            const fov = camera.fov * (Math.PI / 180);
            const aspect = this.container.clientWidth / this.container.clientHeight;

            if (!aspect || isNaN(aspect)) return;

            camera.aspect = aspect;
            camera.updateProjectionMatrix();

            let zDistV = fitR / Math.tan(fov / 2);
            let zDistH = fitR / (Math.tan(fov / 2) * aspect);
            let dist = Math.max(zDistV, zDistH);

            if (!this._origCameraPos) {
                this._origCameraPos = camera.position.clone();
                this._origCameraRot = camera.rotation.clone();
                this._origCameraFov = camera.fov;
            }

            camera.position.set(0, 0, dist);
            camera.lookAt(0, 0, 0);
            this._baseDistToCamera = dist;
        }

        _initAframeScene() {
            const sceneEl = document.getElementById('main-scene');
            
            this._sceneEl = sceneEl;
            this._scene = sceneEl.object3D;
            this._renderer = sceneEl.renderer;
            this._camera = sceneEl.camera;
            this._origEnvMap = this._scene.environment;
            
            const centerPanelContent = document.getElementById('large-center-panel-content');
            if (centerPanelContent && centerPanelContent.object3D) {
                centerPanelContent.object3D.visible = false;
                this._hiddenContent = centerPanelContent;
            }

            const pivotEl = document.createElement('a-entity');
            pivotEl.setAttribute('id', 'news-graph-pivot');
            sceneEl.appendChild(pivotEl);
            this._pivotEl = pivotEl;
            this._pivot = pivotEl.object3D;

            const outerEl = document.createElement('a-entity');
            outerEl.setAttribute('id', 'news-graph-2d-outer');
            pivotEl.appendChild(outerEl);
            this._outerGroupEl = outerEl;
            this._outerGroup = outerEl.object3D;

            const innerEl = document.createElement('a-entity');
            innerEl.setAttribute('id', 'news-graph-2d-inner');
            pivotEl.appendChild(innerEl);
            this._innerGroupEl = innerEl;
            this._innerGroup = innerEl.object3D;

            const lightFront = document.createElement('a-light');
            lightFront.setAttribute('type', 'point');
            lightFront.setAttribute('color', '#ffffff');
            lightFront.setAttribute('intensity', '0.75');
            lightFront.setAttribute('position', '0 0 10');
            pivotEl.appendChild(lightFront);

            const lightRim = document.createElement('a-light');
            lightRim.setAttribute('type', 'ambient');
            lightRim.setAttribute('color', '#cce0ff');
            lightRim.setAttribute('intensity', '0.45');
            pivotEl.appendChild(lightRim);

            const labelRoot = document.createElement('div');
            labelRoot.style.cssText = 'position:absolute;inset:0;z-index:6;pointer-events:none;overflow:hidden;';
            this.container.appendChild(labelRoot);
            this._labelRoot = labelRoot;

            const buildResult = window.NewsGraph.NewsGraphBuilder.buildNodes(this._nodes, this._pivotEl, false, this._links);
            this._meshMap = buildResult.meshMap;
            this._nodeEls = buildResult.nodeEls;
            this._coneInfos = buildResult.coneInfos;

            this._pivot.rotation.set(0, 0, 0);
        }

        _replaceGeometriesWithCircles() {
            const THREE = window.THREE || (window.AFRAME && window.AFRAME.THREE);
            const counts = this._nodes.map(n => n.article_count || 0);
            const minC = Math.min(...counts);
            const maxC = Math.max(...counts);
            const stockMap = this._getTickerPriceMap();

            this._nodeEls.forEach(el => {
                const node = this._nodes.find(n => this._meshMap[n.id] === el);
                if (!node) return;

                const isNarrative = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
                const baseRadius = window.NewsGraph.nodeRadius(node.article_count || 0, minC, maxC);
                const r = baseRadius * (isNarrative ? 0.38 : 0.25);

                const colorStr = window.NewsGraph.getNodeColor(node, stockMap, this._preIpoSymbols);
                const baseOpacity = isNarrative ? 0.90 : 0.75;

                const geo = new THREE.CircleGeometry(r, isNarrative ? 32 : 3);
                const mat = new THREE.MeshBasicMaterial({
                    color: new THREE.Color(colorStr),
                    transparent: true,
                    opacity: baseOpacity,
                    side: THREE.DoubleSide,
                    depthWrite: false
                });
                const mesh = new THREE.Mesh(geo, mat);
                mesh.renderOrder = 2;

                el.removeAttribute('geometry');
                el.removeAttribute('material');

                el.setObject3D('mesh', mesh);

                el.userData.radius = r;
                el.userData.is2DCircle = isNarrative;
                el.userData.is2DTriangle = !isNarrative;

                const newGlowScale = r * 3.2;
                el.userData.baseGlowScale = newGlowScale;
                const glowSprite = el.getObject3D('glowSprite');
                if (glowSprite) {
                    glowSprite.scale.set(newGlowScale, newGlowScale, 1);
                    glowSprite.material.opacity = 0.0;
                }
            });
        }

        _updateEdges() {
            const v1 = new THREE.Vector3();
            const v2 = new THREE.Vector3();

            this._edgeMeshes.forEach(mesh => {
                const elA = this._meshMap[mesh.userData.sourceId];
                const elB = this._meshMap[mesh.userData.targetId];
                if (!elA || !elB) return;

                elA.object3D.getWorldPosition(v1);
                elB.object3D.getWorldPosition(v2);

                this._edgesGroup.worldToLocal(v1);
                this._edgesGroup.worldToLocal(v2);

                const len = v1.distanceTo(v2);
                const angle = Math.atan2(v2.y - v1.y, v2.x - v1.x);

                mesh.position.set((v1.x + v2.x) / 2, (v1.y + v2.y) / 2, -0.015);
                mesh.rotation.z = angle;
                mesh.scale.set(len, mesh.userData.thickness, 1);
            });
        }

        _updateLabels() {
            const W = this.container.clientWidth;
            const H = this.container.clientHeight;
            const v = new THREE.Vector3();
            const centerWorld = new THREE.Vector3();
            const centerScreen = new THREE.Vector3();
            this._outerGroup.getWorldPosition(centerWorld);
            centerScreen.copy(centerWorld).project(this._camera);
            const centerX = (centerScreen.x * 0.5 + 0.5) * W;
            const centerY = (centerScreen.y * -0.5 + 0.5) * H;

            this._nodes.forEach(node => {
                const el = this._meshMap[node.id];
                const div = this._labelMap[node.id];
                if (!el || !div) return;

                v.setFromMatrixPosition(el.object3D.matrixWorld);

                const distToCamera = v.distanceTo(this._camera.position);
                v.project(this._camera);

                if (v.z > 1) { div.style.display = 'none'; return; }

                const sx = (v.x * 0.5 + 0.5) * W;
                const sy = (v.y * -0.5 + 0.5) * H;

                div.style.display = 'block';
                div.style.left = sx + 'px';
                div.style.top = sy + 'px';

                const baseDist = this._baseDistToCamera || 12;
                const scale = Math.max(0.9, Math.min(3.0, baseDist / distToCamera));
                div.style.transform = `translate(-50%, -50%) scale(${scale})`;
                div.style.zIndex = Math.max(1, Math.round(10000 - distToCamera * 500));
            });

            this._sectorLabels.forEach(label => {
                const helper = label.item.helper;
                const div = label.div;

                helper.getWorldPosition(v);
                const distToCamera = v.distanceTo(this._camera.position);
                v.project(this._camera);

                if (v.z > 1) { div.style.display = 'none'; return; }

                const sx = (v.x * 0.5 + 0.5) * W;
                const sy = (v.y * -0.5 + 0.5) * H;

                div.style.display = 'block';
                div.style.left = sx + 'px';
                div.style.top = sy + 'px';

                // Derive the tangent from projected coordinates so both
                // auto-rotation and manual parent rotation are represented.
                let angleDeg = Math.atan2(sy - centerY, sx - centerX) * 180 / Math.PI + 90;

                let normDeg = (angleDeg % 360 + 360) % 360;
                if (normDeg > 90 && normDeg < 270) {
                    angleDeg += 180;
                }

                const baseDist = this._baseDistToCamera || 12;
                const scale = Math.max(0.9, Math.min(3.0, baseDist / distToCamera));

                div.style.transform = `translate(-50%, -50%) rotate(${angleDeg}deg) scale(${scale})`;
                div.style.zIndex = Math.max(1, Math.round(10000 - distToCamera * 500));
            });
        }

        _getTickerPriceMap() {
            return this._tickerPriceMap || new Map();
        }

        _updateTheme() {
            const isLight = this._isLight;
            const defaultLabelColor = isLight ? '#111827' : 'var(--color-text-primary,#e2e8f0)';
            const textShadowLight = 'none';
            const textShadowDark = '0 1px 3px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,1)';

            if (this._labelMap) {
                this._nodes.forEach(node => {
                    const div = this._labelMap[node.id];
                    if (!div) return;
                    div.style.color = defaultLabelColor;
                    div.style.textShadow = isLight ? textShadowLight : textShadowDark;
                });
            }

            if (this._separationRing && this._separationRing.material) {
                this._separationRing.material.color.set(0xffffff);
                this._separationRing.material.needsUpdate = true;
            }

            this._updateNodeVisuals();
        }

        _animate() {
            if (this._destroyed) return;

            this._controls.update();

            if (this._controls.autoRotate) {
                this._outerGroup.rotation.z -= 0.0006;
                this._innerGroup.rotation.z += 0.0006;
            }

            this._keepTickerDirectionsStatic();
            this._updateEdges();
            this._updateLabels();
            this._updateGlowPulse();

            this._animId = requestAnimationFrame(() => this._animate());
        }

        _keepTickerDirectionsStatic() {
            const THREE_NS = window.THREE || (window.AFRAME && window.AFRAME.THREE);
            if (!THREE_NS) return;
            this._pivot.updateMatrixWorld(true);
            const parentWorldQuaternion = new THREE_NS.Quaternion();
            const targetWorldQuaternion = new THREE_NS.Quaternion();
            const zAxis = new THREE_NS.Vector3(0, 0, 1);

            this._nodes.forEach(node => {
                if (window.NewsGraph.NARRATIVE_TYPES.has(node.type)) return;
                const el = this._meshMap[node.id];
                if (!el || !el.object3D.parent || el.userData.staticConeRotation === undefined) return;

                el.object3D.parent.getWorldQuaternion(parentWorldQuaternion);
                targetWorldQuaternion.setFromAxisAngle(zAxis, el.userData.staticConeRotation);
                el.object3D.quaternion.copy(
                    parentWorldQuaternion.clone().invert().multiply(targetWorldQuaternion)
                );
            });
        }

        _updateGlowPulse() {
            const time = performance.now();
            const period = 2000;
            const t = time % period;
            const phase = t / period;
            const wave = 0.5 * (1 - Math.cos(Math.PI * 2 * phase));

            this._nodeEls.forEach(el => {
                if (el.userData && el.userData.isFresh) {
                    const mesh = el.getObject3D('mesh');
                    if (mesh && mesh.material) {
                        if (mesh.material.isMeshBasicMaterial) {
                            if (el.userData.isDimmed) {
                                mesh.material.opacity = 0.15;
                            } else {
                                const minOp = el.userData.opacityMin || 0.6;
                                const maxOp = el.userData.opacityMax || 1.0;
                                mesh.material.opacity = minOp + (maxOp - minOp) * wave;
                            }
                            mesh.material.needsUpdate = true;
                        } else {
                            const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                            materials.forEach(mat => {
                                if (mat && 'emissiveIntensity' in mat) {
                                    const min = el.userData.emissiveMin || 0.05;
                                    const max = el.userData.emissiveMax || 0.65;
                                    mat.emissiveIntensity = min + (max - min) * wave;
                                }
                            });
                        }
                    }

                    const glowSprite = el.getObject3D('glowSprite');
                    if (glowSprite && el.userData.baseGlowScale) {
                        const glowMaxOp = el.userData.isDimmed ? 0.15 : 0.85;
                        glowSprite.material.opacity = wave * glowMaxOp;
                        const scale = el.userData.baseGlowScale * (1.0 + wave * 0.30);
                        glowSprite.scale.set(scale, scale, 1);
                    }
                }
            });
        }

        setOrbitSphereVisible(visible) {
            this.orbitSphereVisible = visible;
            if (this._separationRing) {
                this._separationRing.visible = visible;
            }
        }

        selectNodeById(nodeId, triggerCallback = true) {
            if (!nodeId) {
                this._selectedNodeEl = null;
                this._updateNodeVisuals();
                if (this._controls) {
                    this._controls.enableAutoRotate();
                }
                return;
            }

            const cleanId = String(nodeId).toUpperCase().trim();
            const node = this._nodes.find(n =>
                String(n.id).toUpperCase().trim() === cleanId ||
                String(n.symbol || '').toUpperCase().trim() === cleanId ||
                String(n.label || '').toUpperCase().trim() === cleanId
            );

            if (node) {
                const nodeEl = this._meshMap[node.id];
                if (nodeEl) {
                    this._selectedNodeEl = nodeEl;
                    this._updateNodeVisuals();
                    if (this._controls) {
                        this._controls.disableAutoRotate();
                    }
                    if (triggerCallback && this.onNodeClick) {
                        this.onNodeClick(node);
                    }
                }
            } else {
                this._selectedNodeEl = null;
                this._updateNodeVisuals();
                if (this._controls) {
                    this._controls.enableAutoRotate();
                }
            }
        }

        destroy() {
            this._destroyed = true;
            if (this._animId) cancelAnimationFrame(this._animId);
            if (this._themeObserver) this._themeObserver.disconnect();
            if (this._resizeObserver) this._resizeObserver.disconnect();

            if (this._controls) this._controls.destroy();

            this._labelRoot?.remove();

            if (this._pivotEl && this._pivotEl.parentNode) {
                this._pivotEl.parentNode.removeChild(this._pivotEl);
            }

            if (this._sceneEl && this._sceneEl.object3D) {
                this._sceneEl.object3D.environment = this._origEnvMap || null;
            }
            if (this._sceneEl) {
                const storedConfig = (typeof getCacheItem === 'function') ? getCacheItem('configureControlSettings') : null;
                const isGlowOn = storedConfig ? !!storedConfig.isGlowOn : false;
                const qualityLevel = storedConfig ? storedConfig.qualityLevel || 'quality' : 'quality';
                
                this._sceneEl.setAttribute('bloom', {
                    enabled: isGlowOn && qualityLevel === 'quality',
                    isolateLabels: true
                });
            }

            if (this._hiddenContent && this._hiddenContent.object3D) {
                this._hiddenContent.object3D.visible = true;
            }

            if (this._origCameraPos) {
                this._camera.position.copy(this._origCameraPos);
            }
            if (this._origCameraRot) {
                this._camera.rotation.copy(this._origCameraRot);
            }
        }
    }

    window.NewsGraph2DVisualizer = NewsGraph2DVisualizer;
})();
